import { Component, Injector } from '@angular/core';
import { BaseFormComponent } from '../base-form.component';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { BaseLoginService } from '../../../../services/login/base-login.service';
import { Controller } from '../../../../../../coverpath-client/public/js/Controller';
import { Router } from '@angular/router';
import { INVALID_VALIDATION_LINK } from '../../../../../../common/js/constants/principal';
import { Entity } from '../../../../../../common/js/customer/Entity';
import { Person } from '../../../../../../common/js/customer/Person';
import { PayerTypeOptions } from '../../../../../../common/js/interfaces/CommonTypes';
import type { CoverpathBasePolicy } from '../../../../../classes/coverpath_subclasses/CoverpathBasePolicy';
import { CoverpathCustomer } from '../../../../../classes/coverpath_subclasses/CoverpathCustomer';
import { MMDirectCustomer } from '../../../../../classes/mmdirect_subclasses/MMDirectCustomer';
import { Utils } from '../../../../../../common/js/Utils';
import { UICategory, UILoggerSingleton } from '../../../../../../common/js/logger/UILoggerSingleton';

export type owner = {
  ownerFullName: string;
  ownerFirstName: string;
  ownerEmail: string;
};

@Component({
  moduleId: module.id,
  selector: 'section-verify-your-email',
  templateUrl: './section-verify-your-email.component.html',
})
export class SectionVerifyYourEmailComponent extends BaseFormComponent {
  owners: Array<owner>;
  mainPolicyOwner: owner;
  typeOfOwner: string;
  insuredName: string;
  insuredEmail: string;
  insuredGenderPronoun: string;
  differentFromInsured: boolean;
  validationCode: string;
  customer: CoverpathCustomer | MMDirectCustomer;
  policy: CoverpathBasePolicy;
  sentCode: boolean;
  isError: boolean;
  errorMessage: string;
  emailCode: string;
  successMessage: string;
  isJustVerified: boolean;
  verifyWithAmc: boolean;
  accountEmail: string;
  ownerEmailIsAccountEmail: boolean;
  isOwnerEmailSelected: boolean;
  isEmailValidated: boolean;
  showVerifyEmailSection: boolean;
  isLifeBridge: boolean;
  juvenileHasSigner: boolean;
  signerFirstName: string;
  signerLastName: string;
  signerEmail: string;
  payerFirstName: string;
  payerEmail: string;

  constructor(protected injector: Injector, private amorphicService: AmorphicService, private router: Router) {
    super(injector);
    this.customer = amorphicService.controller.customer;
    this.policy = this.customer.application.primaryPolicy;
    this.sentCode = false;
    this.isJustVerified = false;
    this.accountEmail = this.customer.email;
    this.updateShowVerifyEmailSection();
    this.owners = [];
    if (this.policy.insuredType === this.policy.ownerClassification) {
      this.mainPolicyOwner = {
        ownerFullName: this.policy.insured.person.getFullName(),
        ownerFirstName: this.policy.insured.person.firstName,
        ownerEmail: this.policy.insured.person.email,
      };
      this.differentFromInsured = false;
    } else {
      this.insuredName = this.policy.insured.person.firstName;
      this.insuredEmail = this.policy.insured.person.email;
      this.insuredGenderPronoun = this.policy.getInsuredGenderedPronounUC();
      this.differentFromInsured = true;
    }
    this.fillInOwners();
    this.fillInPayerInfo();
    if (this.policy.insuredIsJuvenile() && this.policy.insured.hasSigner) {
      this.juvenileHasSigner = true;
      this.signerFirstName = this.policy.insured.signer.firstName;
      this.signerLastName = this.policy.insured.signer.lastName;
      this.signerEmail = this.policy.insured.signer.email;
    }
    this.ownerEmailIsAccountEmail = this.isOwnerEmailAccountEmail();
    this.verifyWithAmc = this.applicationService.isAuth0Enabled();
    this.isLifeBridge = this.customer.application.primaryPolicy.isLifeBridge();
  }

  private fillInPayerInfo() {
    switch (this.policy.premiumPayer) {
      case PayerTypeOptions.insured:
        this.payerFirstName = this.insuredName;
        this.payerEmail = this.insuredEmail;
        return;
      case PayerTypeOptions.anotherPerson:
        this.payerFirstName = (<Person>this.policy.payer).firstName;
        this.payerEmail = this.policy.payer.email;
        return;
      case PayerTypeOptions.entity:
        this.payerFirstName = (<Entity>this.policy.payer).authorizedSignerFirstName;
        this.payerEmail = (<Entity>this.policy.payer).authorizedSignerEmail;
        return;
      default:
        this.payerFirstName = undefined;
        this.payerEmail = undefined;
    }
  }

  private fillInOwners() {
    if (!this.mainPolicyOwner) {
      const owner =
        this.policy.jointOwnerIsInsured() && this.policy.insuredType === 'you'
          ? this.policy.otherOwner
          : this.policy.owner;
      this.mainPolicyOwner = {
        ownerFullName: owner.getFullNameOfOwner(),
        ownerFirstName: this.policy.ownerPerson ? owner.emailName : (<Entity>owner).authorizedSigner.firstName,
        ownerEmail: owner.email,
      };
    }
    if (this.policy.ownerEntity && this.policy.ownerEntity.type === 'trust') {
      this.typeOfOwner = 'is an authorized signer';
      const owner = this.policy.ownerEntity;
      const designatedIndex = owner.designatedTrusteeIndex;
      for (let i = 0; i < owner.authorizedTrusteeList.length; i++) {
        if (i === designatedIndex) {
          continue;
        }
        const trustee = owner.authorizedTrusteeList[i];
        if (trustee.trusteePerson) {
          this.owners.push({
            ownerFullName: trustee.trusteePerson.fullName,
            ownerFirstName: trustee.trusteePerson.firstName,
            ownerEmail: trustee.trusteePerson.email,
          });
        } else if (trustee.entityAuthorizedSigner) {
          this.owners.push({
            ownerFullName: trustee.entityAuthorizedSigner.fullName,
            ownerFirstName: trustee.entityAuthorizedSigner.firstName,
            ownerEmail: trustee.entityAuthorizedSigner.email,
          });
        }
      }
    } else if (
      this.policy.isJointOwnerSomeoneElse === 'true' &&
      this.policy.otherOwner &&
      this.policy.otherOwner.fullName
    ) {
      this.typeOfOwner = 'is the joint owner';
      this.owners.push({
        ownerFullName: this.policy.otherOwner.fullName,
        ownerFirstName: this.policy.otherOwner.firstName,
        ownerEmail: this.policy.otherOwner.email,
      });
    } else if (this.policy.contingentOwnerPerson) {
      this.typeOfOwner = 'is the contingent owner';
      this.owners.push({
        ownerFullName: this.policy.contingentOwnerPerson.fullName,
        ownerFirstName: this.policy.contingentOwnerPerson.firstName,
        ownerEmail: this.policy.contingentOwnerPerson.email,
      });
    }
  }

  async ngOnInit() {
    this.isEmailValidated = this.customer.emailValidated;

    /**
     * if not an advisor and the owner email is different than the account email
     * we will display two radio buttons, one for the owner's email and one for the account email
     * default selection is the owner's email
     */
    if (!this.isAdvisor && !this.ownerEmailIsAccountEmail) {
      this.setSelection(true);
    } else {
      this.setSelection(false);
      if (this.verifyWithAmc) {
        await this.checkEmailValidatedWithAmc();
        this.updateShowVerifyEmailSection();
        this.updateProgressAndSubscriptions();
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.isEmailValidated) {
      this.customer.emailValidated = true;
    }
  }

  updateFromModel() {}

  private async checkEmailValidatedWithAmc() {
    const controller: Controller = this.amorphicService.controller as Controller;
    const isEmailValidated: boolean = await controller.checkIfAccountVerified();
    this.customer.emailValidated = isEmailValidated;
  }

  updateShowVerifyEmailSection() {
    this.showVerifyEmailSection = !this.customer.emailValidated && !this.isAdvisor && !this.isOwnerEmailSelected;
  }

  isOwnerEmailAccountEmail(): boolean {
    const ownerEmail =
      this.policy.jointOwnerIsInsured() && this.policy.insuredType === 'you'
        ? this.insuredEmail
        : this.mainPolicyOwner.ownerEmail;
    return Utils.exists(ownerEmail) && ownerEmail.toLowerCase() === this.accountEmail.toLowerCase();
  }

  async setSelection(isOwnerEmailSelected: boolean) {
    this.isOwnerEmailSelected = isOwnerEmailSelected;
    if (isOwnerEmailSelected) {
      this.customer.emailValidated = false;
    } else if (this.isEmailValidated) {
      this.customer.emailValidated = true;
    } else {
      if (this.verifyWithAmc) {
        await this.checkEmailValidatedWithAmc();
      }
    }
    this.updateShowVerifyEmailSection();
    this.updateProgressAndSubscriptions();
  }

  /**
   * function for 'UPDATE ACCOUNT SETTINGS...' button
   * set controller.newEmail = owner's email to default owner's email as the new email in 'Account Settings' page
   * then route to 'Account Settings' page
   */
  navigateToAccountSettings() {
    const controller: Controller = this.amorphicService.controller as Controller;
    controller.newEmail = this.mainPolicyOwner.ownerEmail;
    this.router.navigate(['settings']);
  }

  resetValue() {}

  trigger() {}

  async resendCode(): Promise<void> {
    await this.applicationService.resendEmailVerification();
    this.sentCode = true;
  }

  changeEmail() {}

  async verifyEmail(): Promise<void> {
    if (!this.isAdvisor) {
      const loginService: BaseLoginService = this.injector.get(BaseLoginService);
      const controller: Controller = this.amorphicService.controller as Controller;
      controller.verifyEmailCode = this.emailCode;

      try {
        await loginService.verifyEmail();
        this.successMessage = 'Email successfully verified!';
        this.isJustVerified = true;
        this.isEmailValidated = true;
        await controller.publicSave();
      } catch (error) {
        if (error.code === INVALID_VALIDATION_LINK) {
          this.errorMessage = 'Invalid verification code - make sure you copied correctly from the email';
          this.isError = true;
        } else {
          const err = {
            isHumanRelated: false,
            name: error.name,
            message: error.message,
            stack: error.stack,
          };
          UILoggerSingleton.getInstance().error(
            {
              module: 'sectionVerifyYourEmailComponent',
              function: 'verifyEmail',
              category: UICategory.request,
              error: err,
            },
            'there was a problem verifying the email.'
          );
          this.errorMessage = 'Something went wrong.';
        }
      } finally {
        this.updateProgressAndSubscriptions();
      }
    }
  }

  clearErrors(): void {
    this.errorMessage = '';
    this.isError = false;
  }
}
